import { useEffect, useState } from 'react';
import { StonApiClient } from '@ston-fi/api';
import {
  CONTRACT_PARAMS,
  LIQUIDITY_ENABLED,
  SWAP_ENABLED,
  SWAP_STON_POOL,
} from '../ton/config';
import { createLiquidityPoolSnapshot } from '../ton/liquidity';

const MARKET_ENABLED = SWAP_ENABLED || LIQUIDITY_ENABLED;
const REFRESH_MS = 5 * 60_000;

// The pool read is shared across all mounted consumers: one fetch feeds every
// widget on the page, refreshed on a slow cadence (spot USD price only).
let cachedPrice: number | null = null;
let cachedAt = 0;
let inflight: Promise<number | null> | null = null;

async function fetchPrmdUsd(): Promise<number | null> {
  const info = await new StonApiClient().getPool(SWAP_STON_POOL);
  const pool = createLiquidityPoolSnapshot(info);
  const reservePrmd = Number(pool.reservePrmd);
  const tvlUsd = info.lpTotalSupplyUsd ? Number(info.lpTotalSupplyUsd) : null;
  if (tvlUsd === null || !(reservePrmd > 0)) return null;
  return (tvlUsd / 2) / (reservePrmd / 10 ** CONTRACT_PARAMS.jettonDecimals);
}

function getPrmdUsd(): Promise<number | null> {
  if (cachedPrice !== null && Date.now() - cachedAt < REFRESH_MS) {
    return Promise.resolve(cachedPrice);
  }
  if (!inflight) {
    inflight = fetchPrmdUsd()
      .then((price) => {
        if (price !== null) {
          cachedPrice = price;
          cachedAt = Date.now();
        }
        return price ?? cachedPrice;
      })
      .catch(() => cachedPrice)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Approximate USD price of 1 PRMD from the STON.fi pool; null until known
 *  (or when the market integration is disabled). Best-effort, never throws. */
export function usePrmdUsd(): number | null {
  const [price, setPrice] = useState<number | null>(cachedPrice);
  useEffect(() => {
    if (!MARKET_ENABLED) return;
    let cancelled = false;
    const tick = () => {
      void getPrmdUsd().then((p) => {
        if (!cancelled && p !== null) setPrice(p);
      });
    };
    tick();
    const id = setInterval(tick, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  return price;
}
